/**
 * Layout helper functions for responsive grid and spacing calculations
 */

import { LAYOUT_SPACING } from '../../../../constants/layout'

/**
 * Convert design system padding values to MUI spacing units
 * MUI spacing uses 8px per unit by default
 *
 * @param paddingType - Type of padding ('CONTAINER' or 'SECTION')
 * @returns Responsive padding object with MUI spacing units
 */
export function getResponsivePaddingMUI(paddingType: 'CONTAINER' | 'SECTION' = 'CONTAINER'): {
  xs: number
  sm: number
  md: number
  lg: number
} {
  const padding = LAYOUT_SPACING.RESPONSIVE_PADDING[paddingType]
  return {
    xs: padding.xs / 8, // Convert px to MUI units
    sm: padding.sm / 8,
    md: padding.md / 8,
    lg: padding.lg / 8
  }
}

/**
 * Convert design system gap values to MUI spacing units
 *
 * @returns Responsive gap object with MUI spacing units
 */
export function getResponsiveGapMUI(): { xs: number; sm: number; md: number; lg: number } {
  const gap = LAYOUT_SPACING.RESPONSIVE_GAP.GRID
  return {
    xs: gap.xs / 8,
    sm: gap.sm / 8,
    md: gap.md / 8,
    lg: gap.lg / 8
  }
}

/**
 * Calculate responsive grid columns based on available sections
 *
 * @param hasLeft - Whether left section has data
 * @param hasRight - Whether right section has data
 * @param leftRatio - Percentage for left column when both present (e.g., '40%')
 * @param rightRatio - Percentage for right column when both present (e.g., '60%')
 * @returns Responsive grid template columns configuration
 */
export function getResponsiveGridColumns(
  hasLeft: boolean,
  hasRight: boolean,
  leftRatio: string,
  rightRatio: string
): string | { xs: string; sm: string; md: string; lg: string } {
  // If neither section has data, use single column
  if (!hasLeft && !hasRight) {
    return '1fr'
  }

  // If only one section has data, use single column for all breakpoints
  if (!hasLeft || !hasRight) {
    return '1fr'
  }

  // Both sections present - use responsive two-column layout
  return {
    xs: '1fr', // Mobile: single column
    sm: '1fr', // Tablet: single column
    md: `${leftRatio} ${rightRatio}`, // Small desktop: two columns
    lg: `${leftRatio} ${rightRatio}` // Desktop: two columns
  }
}
